export type LabelPreset =
  | '2.25x1.25'
  | '2.28x1.18'
  | '4x6'
  | 'letter-grid'
  | '2.125x1'
  | '1.875x0.5'

export const LABEL_PRESETS: Record<LabelPreset, { label: string }> = {
  '2.25x1.25': { label: '2.25" × 1.25" label' },
  '2.28x1.18': { label: '2.28" × 1.18" multi-purpose thermal label' },
  '4x6': { label: '4" × 6" shipping label' },
  'letter-grid': { label: 'Letter sheet, 30-up (Avery 5160)' },
  '2.125x1': { label: '1" × 2-1/8" Dymo label' },
  '1.875x0.5': { label: '1/2" × 1-7/8" Dymo label' },
}

function pageCss(preset: LabelPreset): string {
  switch (preset) {
    case '2.25x1.25':
      return '@page { size: 2.25in 1.25in; margin: 0 }'
    case '2.28x1.18':
      return '@page { size: 2.28in 1.18in; margin: 0 }'
    case '4x6':
      return '@page { size: 4in 6in; margin: 0 }'
    case 'letter-grid':
      return '@page { size: letter; margin: 0.5in }'
    case '2.125x1':
      return '@page { size: 2.125in 1in; margin: 0 }'
    case '1.875x0.5':
      return '@page { size: 1.875in 0.5in; margin: 0 }'
  }
}

/**
 * Shared .barcode/.qr/.value sizing assumes at least ~1in of label height.
 * The 2.28x1.18 and 1/2"-tall Dymo presets have their own overrides; the
 * latter is shorter than the 0.75in QR code alone, so it
 * needs its own compact sizing — every other preset is unaffected.
 */
function sizeOverrideCss(preset: LabelPreset): string {
  if (preset === '2.28x1.18') {
    // Fill the whole 2.28 x 1.18in label: large name (wraps to 2 lines, then
    // shrinks via fitNames), barcode and QR side by side, value and price.
    return `
    .label { padding: 0.04in 0.06in; justify-content: space-between; }
    .label .name { font-size: 17px; line-height: 1.1; margin: 0; text-align: center;
                   white-space: normal; text-overflow: clip; max-width: 100%; max-height: 2.2em; }
    .label .codes { display: flex; align-items: center; justify-content: center; gap: 4px;
                    width: 100%; flex: 1 1 auto; min-height: 0; }
    .label .barcode { width: auto; flex: 1 1 auto; min-width: 0; }
    .label .barcode svg { max-height: 0.42in; max-width: 100%; }
    .label .qr { width: 0.5in; height: 0.5in; margin: 0; flex: none; }
    .label .value { font-size: 10px; margin: 0; }
    .label .price { font-size: 15px; margin-top: 0; }
  `
  }
  if (preset !== '1.875x0.5') return ''
  return `
    .label .barcode svg { max-height: 0.26in; }
    .label .qr { width: 0.35in; height: 0.35in; }
    .label .name { font-size: 6px; }
    .label .value { font-size: 6px; }
    .label .price { font-size: 7px; }
  `
}

function labelCss(preset: LabelPreset): string {
  if (preset === 'letter-grid') {
    // Avery 5160-compatible: 3 columns × 10 rows of 2.625in × 1in cells.
    return `
      .sheet { display: grid; grid-template-columns: repeat(3, 2.625in); grid-auto-rows: 1in; }
      .label { display: flex; flex-direction: column; align-items: center; justify-content: center;
               overflow: hidden; page-break-inside: avoid; }
    `
  }
  return `
    .sheet { display: block; }
    .label { display: flex; flex-direction: column; align-items: center; justify-content: center;
             width: 100%; height: 100vh; break-after: page; }
  `
}

export interface LabelContent {
  barcodeSvg: string
  qrDataUrl?: string
  text: string
  /** Optional product name/price, printed above/below the barcode. Either
   *  can be set without the other — an IMEI/ICCID label with just a price,
   *  say — so each is rendered independently. */
  name?: string
  price?: string
}

export function printLabels(opts: LabelContent & { preset: LabelPreset; copies: number }): void {
  const { preset, copies, ...label } = opts
  printLabelSheet({
    labels: Array.from({ length: Math.max(1, copies) }, () => label),
    preset,
  })
}

/**
 * Renders the labels into a hidden same-origin iframe and opens the
 * print dialog on it. An iframe with a document we write ourselves starts
 * with none of the app's CSS, so nothing here needs to be fenced off with
 * `print:hidden` the way an in-page @media print block would — this is the
 * whole document. A fresh window (window.open) was considered and rejected:
 * popup blockers and focus handling make it less reliable than an iframe
 * that's already part of the page.
 */
export function printLabelSheet(opts: { labels: LabelContent[]; preset: LabelPreset }): void {
  const { labels, preset } = opts
  if (labels.length === 0) return
  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  document.body.appendChild(iframe)

  const cleanup = () => {
    iframe.remove()
  }

  const doc = iframe.contentDocument
  if (!doc) {
    cleanup()
    return
  }

  const labelHtml = ({ barcodeSvg, qrDataUrl, text, name, price }: LabelContent) => `
    <div class="label">
      ${name ? `<div class="name">${escapeHtml(name)}</div>` : ''}
      <div class="codes">
        <div class="barcode">${barcodeSvg}</div>
        ${qrDataUrl ? `<img class="qr" src="${qrDataUrl}" alt="" />` : ''}
      </div>
      <div class="value">${escapeHtml(text)}</div>
      ${price ? `<div class="price">${escapeHtml(price)}</div>` : ''}
    </div>
  `

  doc.open()
  doc.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<style>
  ${pageCss(preset)}
  * { box-sizing: border-box; }
  body { margin: 0; font-family: ui-monospace, monospace; }
  ${labelCss(preset)}
  .codes { display: contents; }
  .barcode { display: flex; justify-content: center; width: 100%; }
  .barcode svg { max-width: 90%; height: auto; }
  .qr { width: 0.75in; height: 0.75in; margin-top: 2px; }
  .name { font-family: Arial, Helvetica, 'Liberation Sans', sans-serif; font-size: 10px; font-weight: 700; margin-bottom: 2px; max-width: 95%;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .value { font-size: 10px; letter-spacing: 0.5px; margin-top: 2px; }
  .price { font-size: 12px; font-weight: 700; margin-top: 2px; }
  ${sizeOverrideCss(preset)}
</style>
</head>
<body>
  <div class="sheet">
    ${labels.map(labelHtml).join('')}
  </div>
</body>
</html>`)
  doc.close()

  // The document was written synchronously with doc.write, not loaded over
  // the network, so iframe.onload already fired (or never will) before this
  // point — waiting on it would hang. One rAF is enough for layout to settle.
  iframe.contentWindow?.addEventListener('afterprint', cleanup)
  // Fallback: some browsers never fire afterprint on a torn-down iframe.
  setTimeout(cleanup, 60_000)

  requestAnimationFrame(() => {
    if (preset === '2.28x1.18') {
      // The iframe is 0x0 on screen; give it the label's real size (96 CSS px/in)
      // so the name is measured against the layout that will actually print.
      iframe.style.width = '2.28in'
      iframe.style.height = '1.18in'
      fitNames(doc)
      iframe.style.width = '0'
      iframe.style.height = '0'
    }
    iframe.contentWindow?.focus()
    iframe.contentWindow?.print()
  })
}

/** Steps each name down 1px until it fits in two lines (floor 9px). */
function fitNames(doc: Document): void {
  for (const el of Array.from(doc.querySelectorAll<HTMLElement>('.name'))) {
    let size = 17
    while (size > 9 && el.scrollHeight > el.clientHeight + 1) {
      size -= 1
      el.style.fontSize = `${size}px`
    }
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
